import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, HelpCircle, Loader2, Mail } from "lucide-react";
import { z } from "zod";

import { FechadaNaManutencao } from "@/modules/maintenance/components/FechadaNaManutencao";

/**
 * Recuperar Senha — "The Ethereal Professional" Design System
 *
 * Visual consistency with Login.tsx: same glassmorphism card, background
 * blobs, typography (Manrope/Inter), and ep-* color tokens.
 */

const emailSchema = z.object({
  email: z.string().email("Informe um e-mail válido"),
});

// `VITE_API_URL` JÁ termina em `/api/v1` (produção: "/api/v1"); o caminho nunca
// leva o prefixo. Somá-lo de novo gerava /api/v1/api/v1/... → 401 e nenhum e-mail
// de redefinição saía (28/09/2026).
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1";

const RecuperarSenhaConteudo = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const result = emailSchema.safeParse({ email });
    if (!result.success) {
      setError(result.error.errors[0].message);
      return;
    }

    setIsLoading(true);

    try {
      const resp = await fetch(`${API_URL}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });

      if (resp.status === 429) {
        setError("Muitas tentativas. Aguarde alguns minutos.");
        return;
      }

      // 503 de manutenção (PR 2, 28/09/2026): o backend recusa este endpoint
      // quando a manutenção trava — o login mostra o aviso, então só volta
      // pra lá. `.catch(() => null)`: um 503 sem corpo JSON (ex.: proxy caiu
      // fora de uma manutenção controlada) não pode quebrar aqui.
      if (resp.status === 503) {
        const corpo: { maintenance?: boolean } | null = await resp.json().catch(() => null);
        if (corpo?.maintenance === true) {
          navigate("/login", { replace: true });
          return;
        }
      }

      // Achado do brief: qualquer outro !ok caía direto em `setSuccess(true)`
      // — um 503/500 real mostrava "Verifique seu e-mail" como se tivesse
      // enviado. A resposta 200 continua mostrando sucesso sempre
      // (anti-enumeração — não revela se o e-mail existe).
      if (!resp.ok) {
        setError("Não foi possível enviar agora. Tente novamente em instantes.");
        return;
      }

      setSuccess(true);
    } catch {
      setError("Erro de conexão. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-ep-surface dark:bg-gray-950 font-body text-ep-on-surface dark:text-gray-100 min-h-screen flex flex-col items-center selection:bg-ep-primary-container relative">
      {/* ── Abstract Background Blobs ── */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[10%] -left-[5%] w-[40vw] h-[40vw] rounded-full blur-[120px] bg-ep-primary-fixed dark:bg-ep-primary/30 opacity-20" />
        <div className="absolute bottom-[5%] right-[2%] w-[35vw] h-[35vw] rounded-full blur-[100px] bg-ep-secondary-fixed dark:bg-ep-secondary-fixed/20 opacity-15" />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "url(/logo.png)",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "contain",
            filter: "grayscale(1) brightness(1.5) opacity(0.04)",
            transform: "scale(2) rotate(-5deg)",
          }}
        />
      </div>

      {/* ── Gradient Overlay ── */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          background:
            "radial-gradient(circle at 10% 20%, rgba(178,235,255,0.15) 0%, transparent 40%), " +
            "radial-gradient(circle at 90% 80%, rgba(122,229,232,0.12) 0%, transparent 40%)",
        }}
      />

      {/* ── Header ── */}
      <header className="w-full top-0 left-0 bg-transparent flex justify-between items-center px-4 sm:px-8 py-6 max-w-screen-2xl mx-auto z-20">
        <button
          type="button"
          aria-label="Ajuda"
          className="text-ep-primary dark:text-ep-primary-container hover:opacity-70 transition-opacity"
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      </header>

      {/* ── Main ── */}
      <main className="flex-grow w-full flex items-center justify-center relative px-4 sm:px-6 z-10">
        <div className="w-full max-w-[480px]">
          <div className="bg-white/85 dark:bg-gray-900/85 backdrop-blur-[20px] rounded-xl p-8 sm:p-10 md:p-14 border border-ep-outline-variant/10 dark:border-gray-700/20 shadow-[0_12px_40px_rgba(42,52,57,0.06)] dark:shadow-[0_12px_40px_rgba(0,0,0,0.3)]">
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <img
                src="/logo.png"
                alt="SE Promotora"
                className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                  const fb = (e.target as HTMLImageElement).nextElementSibling;
                  if (fb) (fb as HTMLElement).style.display = "flex";
                }}
              />
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-ep-primary-container to-ep-primary items-center justify-center hidden">
                <span className="text-white font-headline font-extrabold text-xl sm:text-2xl">
                  SE
                </span>
              </div>
            </div>

            {success ? (
              /* ── Success State ── */
              <div className="text-center space-y-6 animate-ep-fade-in">
                <div className="mx-auto w-14 h-14 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h1 className="font-headline text-2xl sm:text-3xl font-extrabold tracking-tight text-ep-primary dark:text-ep-primary-container">
                  Verifique seu e-mail
                </h1>
                <p className="text-ep-on-surface-variant dark:text-gray-400 text-sm leading-relaxed">
                  Se o e-mail estiver cadastrado, enviaremos um link de recuperação. Verifique sua
                  caixa de entrada e a pasta de spam.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="w-full bg-ep-primary dark:bg-ep-primary/90 text-ep-on-primary font-headline font-bold py-3.5 rounded-xl shadow-lg shadow-ep-primary/10 hover:opacity-90 active:scale-[0.98] transition-all duration-300 tracking-wide flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Voltar ao login
                </button>
              </div>
            ) : (
              /* ── Form State ── */
              <>
                <div className="mb-10">
                  <h1 className="font-headline text-2xl sm:text-3xl font-extrabold tracking-tight text-ep-primary dark:text-ep-primary-container mb-3">
                    Recuperar senha
                  </h1>
                  <p className="text-ep-on-surface-variant dark:text-gray-400 font-medium tracking-wide leading-relaxed text-sm sm:text-base">
                    Informe seu e-mail e enviaremos instruções para redefinir sua senha.
                  </p>
                </div>

                {/* Error */}
                {error && (
                  <div
                    role="alert"
                    aria-live="assertive"
                    className="mb-6 text-ep-error dark:text-red-400 text-sm font-medium animate-ep-fade-in"
                  >
                    {error}
                  </div>
                )}

                <form className="space-y-8 sm:space-y-10" onSubmit={handleSubmit}>
                  {/* Email — Floating Label */}
                  <div className="relative group">
                    <input
                      id="recover-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isLoading}
                      placeholder=" "
                      className="peer block w-full bg-transparent border-0 border-b border-ep-outline-variant/20 dark:border-gray-600/30 px-0 py-3 text-ep-on-surface dark:text-gray-100 font-body focus:ring-0 focus:border-ep-primary dark:focus:border-ep-primary-container transition-all duration-300 placeholder-transparent outline-none disabled:opacity-50"
                    />
                    <label
                      htmlFor="recover-email"
                      className="absolute left-0 top-3 text-ep-on-surface-variant/70 dark:text-gray-500 font-label tracking-wide text-sm transition-all duration-300 pointer-events-none peer-focus:-top-4 peer-focus:text-ep-primary dark:peer-focus:text-ep-primary-container peer-focus:text-xs peer-[:not(:placeholder-shown)]:-top-4 peer-[:not(:placeholder-shown)]:text-xs"
                    >
                      E-mail
                    </label>
                  </div>

                  {/* Submit */}
                  <div className="pt-2 sm:pt-4">
                    <button
                      type="submit"
                      disabled={isLoading || !email.trim()}
                      className="w-full bg-ep-primary dark:bg-ep-primary/90 text-ep-on-primary font-headline font-bold py-3.5 sm:py-4 rounded-xl shadow-lg shadow-ep-primary/10 hover:opacity-90 active:scale-[0.98] transition-all duration-300 tracking-wide text-base sm:text-lg disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Mail className="w-5 h-5" />
                          Enviar link de recuperação
                        </>
                      )}
                    </button>
                  </div>
                </form>

                {/* Back to login */}
                <div className="mt-8 text-center">
                  <button
                    type="button"
                    onClick={() => navigate("/login")}
                    className="text-xs font-label uppercase tracking-widest text-ep-primary/70 dark:text-ep-primary-container/70 hover:text-ep-primary dark:hover:text-ep-primary-container transition-colors duration-300 inline-flex items-center gap-1"
                  >
                    <ArrowLeft className="w-3 h-3" />
                    Voltar ao login
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* ── Footer ── */}
      <footer className="w-full bottom-0 bg-transparent flex flex-col md:flex-row justify-between items-center px-4 sm:px-8 py-8 sm:py-12 max-w-screen-2xl mx-auto z-20">
        <p className="text-slate-400 dark:text-slate-600 font-label text-xs tracking-wider uppercase mb-4 md:mb-0">
          &copy; {new Date().getFullYear()} SE Promotora. Todos os direitos reservados.
        </p>
        <div className="flex gap-6 sm:gap-8">
          <span className="text-slate-400 dark:text-slate-600 font-label text-xs tracking-wider uppercase">
            Privacidade
          </span>
          <span className="text-slate-400 dark:text-slate-600 font-label text-xs tracking-wider uppercase">
            Termos
          </span>
          <span className="text-slate-400 dark:text-slate-600 font-label text-xs tracking-wider uppercase">
            Suporte
          </span>
        </div>
      </footer>
    </div>
  );
};

/** Invólucro: fecha a tela e volta pro /login durante a manutenção (o login
 * mostra o aviso) — ver `FechadaNaManutencao`. Os hooks da tela ficam todos
 * dentro de `RecuperarSenhaConteudo`, nunca depois de um return condicional. */
export default function RecuperarSenha() {
  return (
    <FechadaNaManutencao destino="/login">
      <RecuperarSenhaConteudo />
    </FechadaNaManutencao>
  );
}
